import React, { useRef, useState } from "react";

import { twJoin } from "tailwind-merge";

import { Checkbox } from "src/base-components/Checkbox";
import { TextEditor } from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { CommentDb } from "src/clients/flow-api";
import { Button } from "src/design-system/Button";

export const constructCommentDraftKey = (
  versionId: string,
  resourceId: string,
  commentId?: string,
) => {
  return `comment-draft:${versionId}:${resourceId}${commentId ? ":" + commentId : ""}`;
};

type Props = {
  dataLoc?: string;
  onSave: (value: string, review_id?: string) => void;
  autoFocus?: boolean;
  isSaving?: boolean;
  onCancel?: () => void;
  comment?: CommentDb;
  buttonText?: string;
  reviewId?: string;
  initialValue?: string;
  /** Called when the editor content changes. Does not control the value. */
  onChange?: (value: string) => void;
};

export const CommentEditor: React.FC<Props> = ({
  dataLoc,
  comment,
  autoFocus = false,
  isSaving = false,
  buttonText = "Save",
  reviewId,
  onCancel,
  onSave,
  initialValue,
  onChange,
}) => {
  const [internalValue, setInternalValue] = useState(
    initialValue ?? comment?.content ?? "",
  );

  const handleChange = (value: string) => {
    setInternalValue(value);
    onChange?.(value);
  };

  const toolbarRef = useRef<HTMLUListElement>(null);
  const [addToReview, setAddToReview] = useState(true);

  const reviewCheckbox = (
    <Checkbox
      checked={addToReview}
      className="mr-2"
      dataLoc="review-comment-check"
      onChange={(evt) =>
        setAddToReview((evt.target as HTMLInputElement).checked)
      }
    />
  );
  return (
    <div>
      <div
        className={twJoin(
          "flex flex-col rounded border border-gray-200 bg-white p-3",
          "focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-500/25",
          "group",
        )}
      >
        <TextEditor
          autoFocus={autoFocus}
          dataLoc={dataLoc}
          placeholder="Add comment here..."
          toolbarRef={toolbarRef}
          value={internalValue}
          onChange={handleChange}
        />
        <div className="flex justify-between gap-x-3 pt-3">
          <Toolbar ref={toolbarRef} exclude={["heading"]} />
          <div className="flex gap-x-3">
            {onCancel && (
              <Button size="sm" variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              dataLoc={`${dataLoc}-save-button`}
              isDisabled={isSaving}
              isLoading={isSaving}
              size="sm"
              variant="primary"
              onClick={() => {
                onSave(internalValue, addToReview ? reviewId : undefined);
                setInternalValue("");
              }}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
      {reviewId && (
        <div className="flex items-center pt-3 font-inter-normal-13px text-gray-800">
          {reviewCheckbox}
          Add this comment to review process
        </div>
      )}
    </div>
  );
};
